"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { useMarket } from "@app/_context";
import Link from "@components/Link";
import { MarketSelectorButton } from "@components/MarketSelector";
import { Button, Divider } from "@components/ngg";
import { useAuth } from "@context/authContext";
import { useHeaderContext } from "@context/headerContext";
import { useSiteInfo } from "@context/siteInfoContext";
import { ArrowIcon, ArrowRightIcon, UserIcon } from "@ngg/icons";

import { envConfig } from "@/config/env";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useMenuStore } from "@/stores/menuStore";
import { getOpeningHoursText } from "@/utils/common";

const ContactLinks = () => {
  const { locale, contactNavigation: links } = useHeaderContext();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (!links.length) {
    return null;
  }

  return (
    <div className="pt-4 space-y-3">
      {links.map((link, index) => (
        <p key={`${link?.slug}-${index}`} className="text-base font-normal">
          <Link
            locale={locale}
            href={link?.slug}
            onClick={() => setMobileMenuOpen(false)}
          >
            {link?.title}
          </Link>
        </p>
      ))}
    </div>
  );
};

const CustomerServiceInfo = () => {
  const t = useTranslate();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  const {  services, locale  } = useHeaderContext();
  const { contentfulLocale } = useMarket().state.market;
  const customerService = services?.at(0);
  if (!customerService) return null;

  const openingHours = getOpeningHoursText({
    contentfulLocale,
    store: customerService,
    openTodayAt: t("openTodayAt"),
    opensTomorrowAt: t("opensTomorrowAt"),
    opensAtDayOfWeekAt: t("opensAtDayOfWeekAt"),
  });

  return (
    <div className="pt-4">
      <p className="mb-2 text-sm font-normal uppercase">
        {t("contactUs")}
      </p>
      {FEATURE_FLAGS.customerService.phoneLink && (
        <p>
          {t("callUsAt")}
          <a
            className="ml-1 underline"
            href={`tel:${customerService.phoneNumber?.replace(/[-\s]/g, "")}`}
          >
            {customerService.phoneNumber}
          </a>
        </p>
      )}
      {FEATURE_FLAGS.customerService.emailLink && (
        <p>
          {t("emailUsAt")}
          <a
            href={`mailto:${customerService.email}`}
            className="ml-1 underline"
          >
            {customerService?.email}
          </a>
        </p>
      )}

      <p>{openingHours}</p>
      <p>
        <Link
          locale={locale}
          className="underline"
          href={customerService.storePage?.slug}
          onClick={() => setMobileMenuOpen(false)}
        >
          {customerService.storePageLinkText}
        </Link>
        <ArrowIcon className="mb-1 inline -rotate-45 text-xl" />
      </p>
    </div>
  );
};

const StoreInfo = () => {
  const t = useTranslate();
  const {  locale, stores } = useHeaderContext();
  const { contentfulLocale } = useMarket().state.market;
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (!stores?.length) return null;

  return (
    <div className="pt-4">
      <p className="mb-2 text-sm font-normal uppercase">
        {t("ourStores")}
      </p>
      <div className="space-y-4">
        {stores.map((store) => {
          const openingHours = getOpeningHoursText({
            contentfulLocale,
            store,
            openTodayAt: t("openTodayAt"),
            opensTomorrowAt: t("opensTomorrowAt"),
            opensAtDayOfWeekAt: t("opensAtDayOfWeekAt"),
          });
          return (
            <div key={store?.sys?.id}>
              <p>{`${store?.title}, ${store?.address}`}</p>
              <p>{openingHours}</p>
              <Link
                locale={locale}
                className="underline"
                href={store.storePage?.slug}
                onClick={() => setMobileMenuOpen(false)}
              >
                {store?.storePageLinkText}
                <ArrowIcon className="mb-1 inline -rotate-45 text-xl" />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

function UserButton() {
  const t = useTranslate();
  const { myPagesStartPage } = useSiteInfo();
  const { locale  } = useHeaderContext();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);
  const { auth, showLogin } = useAuth();

  if (auth?.user) {
    return (
      <Button
        as={Link}
        href={myPagesStartPage?.slug || ""}
        locale={locale}
        onClick={() => setMobileMenuOpen(false)}
        className="flex w-full items-center gap-1 bg-inherit px-0 py-4 text-black child:max-w-fit lg:hidden [&>*:last-child]:ml-auto"
        endIcon={<ArrowRightIcon className="text-lg" />}
        startIcon={<UserIcon className="text-xl" />}
      >
        {t("myPages")}
      </Button>
    );
  }

  return (
    <Button
      className="flex w-full items-center gap-1 bg-inherit px-0 py-4 text-black child:max-w-fit lg:hidden [&>*:last-child]:ml-auto"
      onClick={() => {
        showLogin();
        setMobileMenuOpen(false);
      }}
      endIcon={<ArrowRightIcon className="text-lg" />}
      startIcon={<UserIcon className="text-xl" />}
    >
      {t("login")}
    </Button>
  );
}

export default function MobileMenuInfo() {
  return (
    <div className="bg-grey-100 px-4 pb-6 lg:hidden">
      <MarketSelectorButton className="w-full content-start gap-1 py-4" />
      {envConfig.NEXT_PUBLIC_NORDICS_FEATURE_FLAG === "true" && (
        <>
          <Divider className="-mx-4 h-px" />
          <UserButton />
        </>
      )}
      <Divider className="-mx-4 h-px" />
      <ContactLinks />
      <Divider className="-mx-4 mt-4 h-px" />
      <CustomerServiceInfo />
      <StoreInfo />
    </div>
  );
}
